"use strict";

const classNames = {
    active: "active",
    visible: "visible",
};

export class Navigation {
    private nav: HTMLElement;
    private mainMenuItems: NodeListOf<HTMLLinkElement>;
    private readonly navButton: HTMLButtonElement;
    private readonly subMenuItems: NodeListOf<HTMLElement>;
    private readonly desktopBreakpoint: number = 1024;
    private isMobile: boolean;
    private isNavActive: boolean = false;

    constructor() {
        this.nav = document.querySelector(".nav");
        this.subMenuItems = this.nav.querySelectorAll(".nav-level-2");

        this.navButton = document.querySelector(".nav-btn");
        this.navButton.addEventListener("click", () => {
            this.navButton.classList.toggle(classNames.active);
            this.nav.classList.toggle(classNames.visible);

            // if nav not active/visible -> open (otherwise close nav/menu)
            if (!this.isNavActive) {
                this.isNavActive = true;
            } else {
                this.closeMenu();
            }
        });

        this.mainMenuItems = this.nav.querySelectorAll('[role="menuitem"]');
        this.mainMenuItems.forEach((menuitem) => {
            const backButton = menuitem.nextElementSibling.querySelector(".back-button");
            backButton.addEventListener("click", () => {
                this.onCloseSubmenuItem(menuitem);
                menuitem.focus();
            });

            menuitem.addEventListener("keydown", this.onKeyDown.bind(this));
            menuitem.addEventListener("click", this.onMenuitemClick.bind(this), {
                capture: true,
            });
        });

        this.closeNavOnOutsideClick();

        this.isMobile = window.innerWidth <= this.desktopBreakpoint;
        window.addEventListener("resize", this.closeNavOnBreakpoint.bind(this));
    }

    /* close/hide the (open) navigation on breakpoint change to prevent buggy behaviour */
    private closeNavOnBreakpoint() {
        if (
            (window.innerWidth <= this.desktopBreakpoint && !this.isMobile) ||
            (window.innerWidth > this.desktopBreakpoint && this.isMobile)
        ) {
            this.closeMenu();
            this.isMobile = !this.isMobile;
        }
    }

    /* close nav if clicked outside nav-link */
    private closeNavOnOutsideClick() {
        document.addEventListener("click", (event) => {
            // return if nav not active/nothing to close
            if (
                !(
                    this.nav.classList.contains(classNames.active) ||
                    this.navButton.classList.contains(classNames.active)
                )
            ) {
                return;
            }

            const clickedElem = event.target;

            if (!(clickedElem instanceof HTMLElement)) {
                this.closeMenu();
                return;
            }

            const isClickInsideNav = this.nav.contains(clickedElem);
            const isNavLinkClick = clickedElem.closest(".nav-link") !== null;
            const isNavButtonClick = clickedElem == this.navButton;
            const isBackButtonClick = clickedElem.classList.contains("back-button") || clickedElem.closest(".nav-btn");

            if (
                !isNavButtonClick &&
                !isBackButtonClick &&
                (!isClickInsideNav || (isClickInsideNav && !isNavLinkClick))
            ) {
                this.closeMenu();
            }
        });
    }

    private updateNavMobileMinHeight(activeNavList?: HTMLElement) {
        if (this.isMobile) {
            this.nav.style.setProperty("--mobile-nav-height", activeNavList ? `${activeNavList.offsetHeight}px` : "");
        } else {
            let maxNavItemHeight = 0;
            this.subMenuItems.forEach((subMenuItem) => {
                const subMenuItemHeight = subMenuItem.offsetHeight;
                if (subMenuItemHeight > maxNavItemHeight) {
                    maxNavItemHeight = subMenuItemHeight;
                }
            });
            this.nav.style.setProperty("--desktop-nav-height", activeNavList ? `${maxNavItemHeight}px` : "");
        }
    }

    private removeActiveMenuItemState() {
        const curActiveItem = this.nav.querySelector(".nav-list-item-level-1.active");
        if (curActiveItem) {
            curActiveItem.classList.remove(classNames.active);
            curActiveItem.querySelector(".nav-link.main").setAttribute("aria-expanded", "false");
        }
    }

    private closeMenu() {
        if (this.isNavActive) {
            this.closeSubmenu();
            this.updateNavMobileMinHeight();

            if (this.isMobile) {
                this.nav.classList.remove(classNames.visible);
                this.navButton.classList.remove(classNames.active);
            }

            this.isNavActive = false;
        }
    }

    /* close submenu by removing active classes */
    private closeSubmenu() {
        this.removeActiveMenuItemState();
        this.nav.classList.remove(classNames.active);
        this.updateNavMobileMinHeight();
    }

    private onCloseSubmenuItem(menuitem: HTMLElement) {
        menuitem.setAttribute("aria-expanded", "false");
        this.closeSubmenu();
    }

    private onOpenSubmenuItem(menuitem: HTMLElement) {
        menuitem.setAttribute("aria-expanded", "true");

        this.removeActiveMenuItemState();

        // set active classes
        this.nav.classList.add(classNames.active);
        menuitem.parentElement.classList.add(classNames.active);
        this.updateNavMobileMinHeight(menuitem.parentElement.querySelector(".nav-level-2"));

        // set focus to back button for mobile nav
        if (this.isMobile) {
            const backButton: HTMLElement = menuitem.nextElementSibling.querySelector(".back-button");
            if (backButton) {
                backButton.focus();
            }
        }

        this.isNavActive = true;
    }

    private onMenuitemSelect(event: KeyboardEvent | MouseEvent) {
        const menuItem = event.currentTarget;

        if (!(menuItem instanceof HTMLElement)) {
            return;
        }

        if (this.nav.classList.contains(classNames.active)) {
            this.onCloseSubmenuItem(menuItem);
        } else {
            this.onOpenSubmenuItem(menuItem);
        }
    }

    private onMenuitemClick(event: MouseEvent) {
        this.onMenuitemSelect(event);
    }

    private onKeyDown(event: KeyboardEvent): void {
        const key = event.key;
        let flag = false;

        switch (key) {
            case " ":
            case "Enter":
                this.onMenuitemSelect(event);
                flag = true;
                break;
            default:
                break;
        }

        if (flag) {
            event.stopPropagation();
            event.preventDefault();
        }
    }
}
